import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {DocumentOption} from '../../documents/document-option';
import {Entity} from '../../entity/entity';
import {DocumentService} from '../../services/document-service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import {LineSelectionDialogComponent} from '../line-selection-dialog/line-selection-dialog.component';
import {EntityLine} from '../../entity/entity-line';
import {DocumentGenerationInput} from '../../documents/document-generation-input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { EntityKeyUtils } from "../../utils/entity-key-utils";
import { LeadTimeUtils } from "../../utils/lead-time-utils";


@Component({
    selector: 'document-generation-dialog',
    templateUrl: './document-generation-dialog.component.html',
    styleUrls: ['./document-generation-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatRadioGroup, FormsModule, MatRadioButton, MatButton, MatTooltip, MatDialogActions]
})
export class DocumentGenerationDialogComponent {
  private readonly documentService = inject(DocumentService);
  private dialogRef = inject<MatDialogRef<DocumentGenerationDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly data = inject(MAT_DIALOG_DATA);


  public entity: Entity;
  public documentOptions: DocumentOption[];
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  public selectedDocuments: string[];
  private timeoutMinutes = 2;
  private timeoutValue = this.timeoutMinutes * 60 * 1000;
  private timeoutMessage = 'Generating the selected documents is taking longer than expected.'
    + '\nPlease come back in a few minutes to view the documents';
  private readonly allEntityLineIds: string[];
  lineSelection = 'all';
  readonly selectedLines = signal<string>(undefined!);

  constructor
  () {
    const data = this.data;

    this.entity = data.entity;
    this.documentOptions = this.createDocumentOptionList();
    if (this.isSchedule()) {
      this.selectedDocuments = [];
    } else {
      this.selectedDocuments = ['TRANSMITTAL'];
    }
    this.allEntityLineIds = data.allLines;
  }

  isSchedule(): boolean {
    return 'SC' === (this.entity?.releaseTypeId || '');
  }

  private createDocumentOptionList(): DocumentOption[] {
    const options: DocumentOption[] = [];
    options.push(new DocumentOption('RATING_SCHEDULE', 'Rating Schedule', 'CSV', ['CSV']));
    if (!this.isSchedule()) {
      options.push(new DocumentOption('TRANSMITTAL', 'Transmittal', 'EXCEL', ['EXCEL', 'WORD']));
    }
    options.push(new DocumentOption('DIMENSIONAL_DRAWING', 'Dimensional Drawing', 'PDF', ['PDF']));
    options.push(new DocumentOption('MECHANICAL_SPECS', 'Mechanical Specs', 'WORD', ['WORD', 'PDF']));
    options.push(new DocumentOption('RATINGS_DATA', 'Ratings Data', 'PDF', ['PDF']));
    options.push(new DocumentOption('PRODUCT_SUBMITTAL_COVER_SHEET', 'Product Submittal Cover Sheet', 'WORD', ['WORD']));
    options.push(new DocumentOption('UNIT_INFORMATION', 'Unit Information', 'EXCEL', ['EXCEL']));
    options.push(new DocumentOption('SHIPPING_SPECIFICATION', 'Shipping Specification', 'PDF', ['PDF']));
    if (!this.isSchedule()) {
      options.push(new DocumentOption('VALVE_PACKAGE_SUBMITTAL', 'Valve Package Submittal', 'PDF', ['PDF']));
      options.push(new DocumentOption('ELECTRICAL_SUBMITTAL', 'Electrical Submittal', 'PDF', ['PDF']));
      options.push(new DocumentOption('COMPONENT_CUT_SHEET', 'Component Cut-Sheet', 'PDF', ['PDF']));
    }
    if(this.entity.repOfficeId === '9100' && !LeadTimeUtils.isPartsOrder(this.entity.leadTimeCode)){
      options.push(new DocumentOption('CUSTOM_EXPORT_FORMAT', 'Custom Export', 'EXCEL', ['EXCEL']));
    }

    return options;
  }

  public saveAndClose(): void {
    this.isBusy.set(true);
    const selectedOptions: DocumentOption[] = this.documentOptions.filter(option => this.isSelected(option));
    if (selectedOptions.length === 0) {
      this.errorMessage.set('No documents were selected to generate');
      this.isBusy.set(false);
    } else {
      let lineIds: string[] = [];
      if (this.lineSelection === 'all') {
        lineIds = this.allEntityLineIds;
      } else {
        if ((this.selectedLines() || '').length === 0) {
          this.errorMessage.set('No lines were selected for the documents');
          this.isBusy.set(false);
        } else {
          lineIds = this.selectedLines().split(',');
        }
      }
      if (lineIds.length > 0) {
        selectedOptions.forEach(option => option.lineIds = lineIds);
        const input: DocumentGenerationInput = new DocumentGenerationInput();
        input.options = selectedOptions;
        input.entityId = this.entity.id.entityId;
        input.revision = this.entity.id.entityRevision;
        const subscription = this.documentService.generate(input).subscribe({
          next: () => this.dialogRef.close(null),
          error: () => this.handleError()
        });
        setTimeout(() => {
          subscription.unsubscribe();
          this.dialogRef.close(this.timeoutMessage);
        }, this.timeoutValue);
      }
    }
  }

  public generateSubmittalPackage(): void {
    this.isBusy.set(true);
    const selectedOptions: DocumentOption[] = this.documentOptions.filter(option => this.isSelected(option));
    if (selectedOptions.length === 0) {
      this.errorMessage.set('No documents were selected to generate');
      this.isBusy.set(false);
    } else {
      let lineIds: string[] = [];
      if (this.lineSelection === 'all') {
        lineIds = this.allEntityLineIds;
      } else {
        if ((this.selectedLines() || '').length === 0) {
          this.errorMessage.set('No lines were selected for the documents');
          this.isBusy.set(false);
        } else {
          lineIds = this.selectedLines().split(',');
        }
      }
      if (lineIds.length > 0) {
        selectedOptions.forEach(option => option.lineIds = lineIds);
        const input: DocumentGenerationInput = new DocumentGenerationInput();
        input.options = selectedOptions;
        input.entityId = this.entity.id.entityId;
        input.revision = this.entity.id.entityRevision;
        const subscription = this.documentService.generateProjectSubmittal(input).subscribe({
          next: () => this.dialogRef.close(null),
          error: () => this.handleError()
        });
        setTimeout(() => {
          subscription.unsubscribe();
          this.dialogRef.close(this.timeoutMessage);
        }, this.timeoutValue);
      }
    }
  }

  public updateSelection(documentOption: DocumentOption): void {
    if (this.isSelected(documentOption)) {
      this.selectedDocuments.splice(this.selectedDocuments.indexOf(documentOption.documentType, 0), 1);
    } else {
      this.selectedDocuments.push(documentOption.documentType);
    }
  }

  private handleError(): void {
    this.isBusy.set(false);
    this.errorMessage.set('There was an error generating the documents.');
  }

  public toggleAllSelections(): void {
    if (this.allItemsSelected()) {
      this.selectedDocuments = [];
    } else {
      this.selectedDocuments = this.documentOptions.map(docOption => docOption.documentType);
    }
  }

  public allItemsSelected(): boolean {
    return this.selectedDocuments.length === this.documentOptions.length;
  }

  public isSelected(documentOption: DocumentOption) {
    return this.selectedDocuments.indexOf(documentOption.documentType, 0) > -1;
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  selectLines(): void {
    this.errorMessage.set(undefined!);
    const selectionDialog = this.dialog.open(LineSelectionDialogComponent, {
      width: '1064px',
      height: '768px',
      data: {entityKey: this.entity.id, selectedLines: this.selectedLines()},
    });
    selectionDialog.afterClosed().subscribe((result: EntityLine[]) => {
      if (result && result.length > 0) {
        this.selectedLines.set(result.map(line => line.lineId)
          .sort((first, second) => {
            const firstAsNumber: number = +first;
            const secondAsNumber: number = +second;
            if (isNaN(firstAsNumber) || isNaN(secondAsNumber)) {
              return first.localeCompare(second);
            } else {
              return firstAsNumber - secondAsNumber;
            }
          }).join(','));
      }
    });
  }

  clearLineSelection(): void {
    this.selectedLines.set(undefined!);
  }

}

